import React, { useMemo, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import _ from "lodash";
import { useSelector } from "react-redux";
import { getProjContractorOptions } from "../../_helpers/contractorOptions";
import { getLaborOption } from "./utils/adrUtilsV3";
import { getTradeOption } from "../../_helpers/tradeOptions";
import "./styles/adrTentativeAgreement.css";
import AlertBox from "../_common/AlertBox";

/**
 * Container for TaLaborSetRows
 * @param {Object} props
 */
export function AdrTentativeAgreementModal_LaborSetV3(props) {
  const {
    tabId,
    projectId,
    status,
    isLocked,
    laborsClient,
    updateLabor,
    selectedContractors,
    changeJustificationModal,
  } = props;

  const laborSetOrder = useSelector((state) =>
    state.adr.openAdrs[tabId] ? state.adr.openAdrs[tabId].laborSetOrder || [] : null
  );
  const laborSetClient = useSelector((state) =>
    state.adr.openAdrs[tabId] ? state.adr.openAdrs[tabId].laborSetClient || null : null
  );
  const projContractorList = useSelector(
    (state) => state.contractors.projContractorLists[projectId] || null
  );
  const projLaborList = useSelector(
    (state) => state.labors.projLaborLists[projectId] || null
  );
  const tradeList = useSelector((state) => state.feederLists.tradeList || null);

  const contractorOptions = useMemo(
    () =>
      projContractorList
        ? getProjContractorOptions(
            projContractorList.filter((c) => selectedContractors.includes(c.contractorId))
          )
        : null,
    [projContractorList, selectedContractors]
  );

  const laborOptions = useMemo(
    () =>
      projLaborList
        ? projLaborList
            .filter((l) => selectedContractors.includes(l.contractorId))
            .map((l) => {
              const trade = l.defaultTradeTradeId
                ? tradeList.find((t) => t.tradeId === l.defaultTradeTradeId)
                : null;
              return {
                ...getLaborOption(l, trade ? trade.title : null),
                fullName: l.fullName, // This is to only show the fullName when selected
                contractorId: l.contractorId,
                defaultTradeTradeId: l.defaultTradeTradeId,
              };
            })
        : null,
    [projLaborList, tradeList, selectedContractors]
  );

  const tradeOptions = useMemo(
    () => (tradeList ? tradeList.map((t) => getTradeOption(t)) : null),
    [tradeList]
  );

  // Filter out any rows without contractorId, laborId or tradeId, and those
  // with contractorIds not included in selectedContractors
  const validLaborSet =
    laborSetOrder && laborSetClient
      ? laborSetOrder.filter((lsrKey) => {
          const taLaborKey = `${laborSetClient[lsrKey].laborId}.${laborSetClient[lsrKey].tradeId}`;
          return (
            taLaborKey in laborsClient &&
            laborsClient[taLaborKey].contractorId &&
            laborsClient[taLaborKey].laborId &&
            laborsClient[taLaborKey].tradeId &&
            selectedContractors.includes(laborsClient[taLaborKey].contractorId)
          );
        })
      : [];

  const renderHeader = () => {
    return (
      <div className="d-flex flex-row flex-wrap ta-wp__tr">
        <div className="ta-wp__th ta-wp__th--contractor">
          <span>Contractor</span>
        </div>
        <div className="ta-wp__th ta-wp__th--labor-name">
          <span>Name (Default Trade)</span>
        </div>
        <div className="ta-wp__th ta-wp__th--labor-trade">
          <span>Trade</span>
        </div>
        <div className="ta-wp__th ta-wp__th--hours">
          <span className="badge badge-info px-1 my-auto">RT</span>
        </div>
        <div className="ta-wp__th ta-wp__th--hours">
          <span className="badge badge-warning px-1 my-auto">OT</span>
        </div>
        <div className="ta-wp__th ta-wp__th--select">
          <span className="my-auto">
            <small>
              <b>TA Work?</b>
            </small>
          </span>
        </div>
      </div>
    );
  };

  return (
    <React.Fragment>
      <div className="w-100 p-1">
        <AlertBox color="info" icon="info-circle">
          To update your daily report with these hours, check "Apply TA hours to the daily
          report" below.
        </AlertBox>
      </div>
      {renderHeader()}
      <div className="d-flex flex-column w-100 ta-wp">
        {validLaborSet.length > 0 ? (
          validLaborSet.map((rowKey) => {
            const lsr = laborSetClient[rowKey];
            const taLaborKey = `${lsr.laborId}.${lsr.tradeId}`;
            return (
              <AdrTentativeAgreementModal_LaborSetRowV3
                key={taLaborKey}
                rowKey={taLaborKey}
                status={status}
                isLocked={isLocked}
                taLabor={laborsClient[taLaborKey]}
                contractorOptions={contractorOptions}
                laborOptions={laborOptions}
                tradeOptions={tradeOptions}
                updateLabor={updateLabor}
                changeJustificationModal={changeJustificationModal}
              />
            );
          })
        ) : (
          <div className="ta-wp__tr">
            <div className="ta-wp__td">
              <input
                name="noLaborsToShow"
                id="noLaborsToShow"
                className="form-control"
                value={"There are no laborers selected for this TA."}
                onChange={() => {}}
                disabled={true}
              />
            </div>
          </div>
        )}
      </div>
    </React.Fragment>
  );
}

function AdrTentativeAgreementModal_LaborSetRowV3(props) {
  const {
    rowKey,
    status,
    isLocked,
    contractorOptions,
    laborOptions,
    tradeOptions,
    taLabor,
    updateLabor,
    changeJustificationModal,
  } = props;

  const [showChangeJustificationModal, _toggleChangeJustificationModal] = useState(false);
  const toggleChangeJustificationModal = () =>
    _toggleChangeJustificationModal(!showChangeJustificationModal);

  const cachedValues = useRef({
    regHours: taLabor.regHours,
    otHours: taLabor.otHours,
    excludeFromTa: taLabor.excludeFromTa,
  });
  const changeAnnotation = useRef("");

  const selectedContractor = useMemo(
    () => contractorOptions.find((c) => c.value === taLabor.contractorId) || null,
    [taLabor.contractorId]
  );
  const selectedLabor = useMemo(
    () => laborOptions.find((l) => l.value === taLabor.laborId) || null,
    [taLabor.laborId]
  );
  const selectedTrade = useMemo(
    () => tradeOptions.find((t) => t.value === taLabor.tradeId) || null,
    [taLabor.tradeId]
  );

  const updateCachedValues = () => {
    cachedValues.current = {
      regHours: taLabor.regHours,
      otHours: taLabor.otHours,
      excludeFromTa: taLabor.excludeFromTa,
    };
  };

  const cancelChange = () => {
    updateLabor(rowKey, {
      regHours: cachedValues.current.regHours,
      otHours: cachedValues.current.otHours,
      excludeFromTa: cachedValues.current.excludeFromTa,
    });
  };

  const handleChange_excludeFromTa = (e) => {
    updateLabor(rowKey, {
      excludeFromTa: !e.target.checked,
    });
    if (isLocked) {
      const actionLabel = e.target.checked ? "Added" : "Removed";
      changeAnnotation.current = `${actionLabel} ${
        selectedLabor.label
      }, (with reg. hours of ${+taLabor.regHours || 0} and OT hours of ${
        +taLabor.otHours || 0
      }) ${e.target.checked ? "to" : "from"} this TA.`;
      toggleChangeJustificationModal();
    }
  };

  const handleChange_laborHours = (field) => (e) =>
    updateLabor(rowKey, { [field]: e.target.value });

  const handleBlur_laborHours = (field) => (e) => {
    if (isLocked && (+cachedValues.current[field] || 0) !== (+taLabor[field] || 0)) {
      const fieldLabel =
        field === "regHours" ? "reg. hours" : field === "otHours" ? "OT Hours" : "";

      // Example: "Changed reg. hours for John Smith from 4 to 2"
      changeAnnotation.current = `Changed ${fieldLabel} for ${selectedLabor.label} from ${
        +cachedValues.current[field] || "0"
      } to ${e.target.value}.`;

      toggleChangeJustificationModal();
    }
  };

  return (
    <React.Fragment>
      <div className="d-flex flex-row flex-wrap ta-wp__tr">
        <div className="ta-wp__td ta-wp__td--contractor">
          <input
            name="contractorId"
            id={`laborContractorSelectId_${rowKey}`}
            className="form-control"
            value={selectedContractor.label}
            onChange={() => {}}
            disabled={true}
          />
        </div>
        <div className="ta-wp__td ta-wp__td--labor-name">
          <input
            name="laborId"
            id={`laborSelectId_${rowKey}`}
            value={selectedLabor.label}
            className="form-control"
            onChange={() => {}}
            disabled={true}
          />
        </div>
        <div className="ta-wp__td ta-wp__td--labor-trade">
          <input
            name="tradeId"
            id={`tradeSelectId_${rowKey}`}
            value={selectedTrade.label}
            onChange={() => {}}
            className="form-control"
            disabled={true}
          />
        </div>
        <div className="ta-wp__td ta-wp__td--hours">
          <input
            className="form-control px-1"
            type="number"
            id={`laborRegHoursId_${rowKey}`}
            name={`laborRegHours_${rowKey}`}
            value={taLabor.regHours || ""}
            onChange={handleChange_laborHours("regHours")}
            onBlur={handleBlur_laborHours("regHours")}
            autoComplete="off"
            disabled={taLabor.excludeFromTa || status.isReadOnly}
          />
        </div>
        <div className="ta-wp__td ta-wp__td--hours">
          <input
            className="form-control px-1"
            type="number"
            id={`laborOtHoursId_${rowKey}`}
            name={`laborOtHours_${rowKey}`}
            value={taLabor.otHours || ""}
            onChange={handleChange_laborHours("otHours")}
            onBlur={handleBlur_laborHours("otHours")}
            autoComplete="off"
            disabled={taLabor.excludeFromTa || status.isReadOnly}
          />
        </div>
        <div className="ta-wp__td ta-wp__td--select p-1">
          <input
            type="checkbox"
            name="includeInTa"
            className="align-middle py-auto"
            id={`taLaborSelect_${rowKey}`}
            style={{ width: "1.6rem", height: "1.6rem" }}
            value={!taLabor.excludeFromTa}
            checked={!taLabor.excludeFromTa}
            onTouchEnd={handleChange_excludeFromTa}
            onChange={handleChange_excludeFromTa}
            disabled={status.isReadOnly}
          />
        </div>
        <div className="ta-wp__td ta-wp__td--delete"></div>
      </div>
      {showChangeJustificationModal &&
        changeJustificationModal({
          rowKey,
          showModal: showChangeJustificationModal,
          toggleModal: toggleChangeJustificationModal,
          changeAnnotation: changeAnnotation.current,
          cancelChange,
          updateCachedValues,
        })}
    </React.Fragment>
  );
}
